import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import {
  Activity,
  Bell,
  BellRing,
  Compass,
  Database,
  LayoutDashboard,
  type LucideIcon,
  Search,
} from 'lucide-react';
import { type KeyboardEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/ui/spinner';
import { usePaletteStore } from '@/stores/palette';
import { ago } from '@/widgets/_sdk/format';
import { type PaletteItem, type PaletteItemType, usePaletteSearch } from './use-palette-search';

/**
 * Command palette V1 (PRD VS-070): Ctrl+K / Cmd+K anywhere once signed in.
 * A combobox driving a listbox (WAI-ARIA APG): focus stays in the input and
 * aria-activedescendant points at the active option.
 */

const ICONS: Record<PaletteItemType, LucideIcon> = {
  page: Compass,
  dashboard: LayoutDashboard,
  source: Database,
  alert: Bell,
  rule: BellRing,
  event: Activity,
};

const isPaletteShortcut = (event: globalThis.KeyboardEvent) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  event.key.toLowerCase() === 'k';

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-sm border bg-background px-1.5 font-mono text-[0.6875rem] leading-5 text-muted-foreground">
      {children}
    </kbd>
  );
}

export function CommandPalette() {
  const { t } = useTranslation('palette');
  const open = usePaletteStore((s) => s.open);
  const setOpen = usePaletteStore((s) => s.setOpen);
  const toggle = usePaletteStore((s) => s.toggle);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!isPaletteShortcut(event)) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-enter bg-black/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="glass fixed left-1/2 top-[12vh] z-50 flex w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 origin-top animate-enter flex-col overflow-hidden text-card-foreground"
        >
          <DialogPrimitive.Title className="sr-only">{t('title')}</DialogPrimitive.Title>
          <PalettePanel onClose={() => setOpen(false)} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Mounted only while open, so every opening starts from an empty query. */
function PalettePanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('palette');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const { sections, pending, error, record } = usePaletteSearch(query);
  const listId = useId();

  const flat = sections.flatMap((s) => s.items);
  const current = Math.min(active, flat.length - 1);
  const optionId = (index: number) => `${listId}-o${index}`;

  useEffect(() => {
    if (current >= 0)
      document.getElementById(optionId(current))?.scrollIntoView?.({ block: 'nearest' });
  });

  const pick = (item: PaletteItem) => {
    record(item);
    onClose();
    void navigate({ href: item.href });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const last = flat.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: current >= last ? 0 : current + 1,
      ArrowUp: current <= 0 ? last : current - 1,
      Home: 0,
      End: last,
    };
    const target = moves[event.key];
    if (target !== undefined && flat.length > 0) {
      event.preventDefault();
      setActive(target);
      return;
    }
    const item = flat[current];
    if (event.key === 'Enter' && item) {
      event.preventDefault();
      pick(item);
    }
  };

  let index = -1;
  return (
    <>
      <div className="flex items-center gap-3 border-b px-4">
        <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          role="combobox"
          aria-expanded={flat.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={current >= 0 ? optionId(current) : undefined}
          aria-label={t('inputLabel')}
          placeholder={t('placeholder')}
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          className="h-14 min-w-0 flex-1 bg-transparent text-base outline-hidden placeholder:text-muted-foreground"
        />
        {pending && <Spinner className="text-muted-foreground" />}
      </div>

      {error && (
        <p role="alert" className="border-b px-4 py-2 text-xs text-destructive">
          {t('remoteError')}
        </p>
      )}

      <output aria-live="polite" className="sr-only">
        {pending ? t('searching') : t('count', { count: flat.length })}
      </output>

      <div
        id={listId}
        role="listbox"
        aria-label={t('resultsLabel')}
        className="max-h-[min(60vh,28rem)] scroll-py-2 overflow-y-auto overscroll-contain px-2 empty:hidden"
      >
        {sections.map((section) => {
          const headerId = `${listId}-${section.group}`;
          return (
            // biome-ignore lint/a11y/useSemanticElements: a listbox group is role="group" (APG); a fieldset is a form control group
            <div
              key={section.group}
              role="group"
              aria-labelledby={headerId}
              className="py-2 not-first:border-t"
            >
              <div id={headerId} className="px-3 pb-1 text-xs font-medium text-muted-foreground">
                {t(`groups.${section.group}`)}
              </div>
              {section.items.map((item) => {
                index += 1;
                const i = index;
                const Icon = ICONS[item.type];
                return (
                  // biome-ignore lint/a11y/useKeyWithClickEvents: keys are handled on the combobox input (APG pattern)
                  <div
                    key={`${item.type}:${item.id}`}
                    id={optionId(i)}
                    role="option"
                    tabIndex={-1}
                    aria-selected={i === current}
                    onClick={() => pick(item)}
                    onMouseMove={() => i !== current && setActive(i)}
                    className="relative flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-[90ms] before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-primary before:opacity-0 aria-selected:bg-muted aria-selected:before:opacity-100"
                  >
                    <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {item.title}
                      {item.subtitle && (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          {item.subtitle}
                        </span>
                      )}
                    </span>
                    {item.at && (
                      <time
                        dateTime={item.at}
                        className="shrink-0 font-mono text-xs text-muted-foreground tabular-nums"
                      >
                        {ago(item.at)}
                      </time>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {query.trim() !== '' && flat.length === 0 && !pending && (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          {t('empty', { query: query.trim() })}
        </p>
      )}

      <div className="hidden items-center gap-4 border-t px-4 py-2 text-xs text-muted-foreground sm:flex">
        <span className="inline-flex items-center gap-1.5">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          {t('hints.move')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Kbd>{t('hints.enter')}</Kbd>
          {t('hints.open')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Kbd>{t('hints.escape')}</Kbd>
          {t('hints.close')}
        </span>
      </div>
    </>
  );
}
